// CareLogix™ ORACLE — OracleChartIntelligence (Phase 33 §2)
// Premium animated SVG sparkline + entry/target/stop zones, tier-aware depth.
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE, buildSparkline, scoreColor } from "@/components/animation/MotionShared";
import { predictiveApi } from "@/api/phase33Client";
import { useMembership } from "@/components/membership/MembershipContext";

function toPath(points, w, h, pad = 6) {
  if (!points || !points.length) return "";
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(0.001, max - min);
  const stepX = (w - pad * 2) / Math.max(1, points.length - 1);
  return points
    .map((v, i) => {
      const x = pad + i * stepX;
      const y = h - pad - ((v - min) / span) * (h - pad * 2);
      return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
}

export default function OracleChartIntelligence({ symbol = "AAPL", domain = "stocks", height = 220, testid = "oracle-chart-intel" }) {
  const { tier } = useMembership();
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    predictiveApi.signal(symbol, domain)
      .then((r) => { if (!cancelled) setSignal(r.data); })
      .catch(() => { if (!cancelled) setErr("Could not load chart intelligence."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [symbol, domain]);

  const sparks = useMemo(() => buildSparkline({ seed: `${symbol}-${domain}`, points: 64, base: signal?.entry_exit?.price_reference || 120, vol: 3.5 }), [symbol, domain, signal]);
  const W = 720, H = height;
  const path = toPath(sparks, W, H, 8);
  const last = sparks[sparks.length - 1] || 0;
  const first = sparks[0] || 0;
  const change = last - first;
  const pct = first !== 0 ? (change / first) * 100 : 0;
  const positive = change >= 0;
  const tierFull = tier === "enterprise" || tier === "executive";
  const showZones = tier !== "free";

  return (
    <section style={S.shell} data-testid={testid} aria-label="ORACLE Chart Intelligence">
      <header style={S.head}>
        <div>
          <div style={S.eyebrow}>ORACLE CHART INTELLIGENCE™</div>
          <h3 style={S.title} data-testid={`${testid}-title`}>{symbol} · <span style={S.titleSub}>{domain.toUpperCase()}</span></h3>
        </div>
        <div style={S.kpis}>
          <span style={{ ...S.kpiPill, color: positive ? PALETTE.greenAccent : PALETTE.red, borderColor: positive ? PALETTE.greenAccent : PALETTE.red }}>
            {positive ? "▲" : "▼"} {pct.toFixed(2)}%
          </span>
          {signal?.scores?.confidence_score != null && (
            <span style={{ ...S.kpiPill, color: scoreColor(signal.scores.confidence_score), borderColor: scoreColor(signal.scores.confidence_score) }}>
              CONF {signal.scores.confidence_score}
            </span>
          )}
          {signal?.scores?.opportunity_score != null && (
            <span style={{ ...S.kpiPill, color: scoreColor(signal.scores.opportunity_score), borderColor: scoreColor(signal.scores.opportunity_score) }}>
              OPP {signal.scores.opportunity_score}
            </span>
          )}
        </div>
      </header>

      <div style={{ position: "relative" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="Price sparkline with predictive overlays" data-testid={`${testid}-svg`}>
          <defs>
            <linearGradient id={`${testid}-fill`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"  stopColor={PALETTE.gold} stopOpacity="0.30" />
              <stop offset="100%" stopColor={PALETTE.gold} stopOpacity="0" />
            </linearGradient>
            <linearGradient id={`${testid}-stroke`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%"   stopColor={PALETTE.goldBright} />
              <stop offset="100%" stopColor={PALETTE.gold} />
            </linearGradient>
          </defs>
          {Array.from({ length: 5 }).map((_, i) => (
            <line key={i} x1="0" x2={W} y1={(H / 5) * (i + 1)} y2={(H / 5) * (i + 1)} stroke="rgba(197,160,89,0.06)" />
          ))}
          <motion.path
            d={`${path} L ${W - 8},${H} L 8,${H} Z`}
            fill={`url(#${testid}-fill)`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          />
          <motion.path
            d={path}
            fill="none"
            stroke={`url(#${testid}-stroke)`}
            strokeWidth="2"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
          />
          {showZones && signal?.entry_exit && (
            <g data-testid={`${testid}-zones`}>
              <rect x="0" y={H * 0.34} width={W} height={H * 0.10} fill="rgba(16,185,129,0.08)" />
              <rect x="0" y={H * 0.62} width={W} height={H * 0.10} fill="rgba(239,68,68,0.08)" />
              <text x="8" y={H * 0.40} fontSize="10" fill={PALETTE.greenAccent} fontFamily="'Space Mono', monospace">ENTRY ${signal.entry_exit.entry_range[0]} – ${signal.entry_exit.entry_range[1]}</text>
              <text x="8" y={H * 0.68} fontSize="10" fill={PALETTE.red} fontFamily="'Space Mono', monospace">STOP ${signal.entry_exit.stop_range[0]}</text>
              <text x={W - 200} y={H * 0.30} fontSize="10" fill={PALETTE.goldBright} fontFamily="'Space Mono', monospace">TARGET ${signal.entry_exit.target_range[0]} – ${signal.entry_exit.target_range[1]}</text>
            </g>
          )}
          {!showZones && (
            <g>
              <text x={W / 2} y={H / 2} fontSize="12" fill={PALETTE.gold} textAnchor="middle" fontFamily="'Space Mono', monospace" data-testid={`${testid}-upgrade-cta`}>
                Upgrade to unlock entry / target / stop zones
              </text>
            </g>
          )}
        </svg>
        {loading && <div style={S.overlay} data-testid={`${testid}-loading`}>Loading ORACLE chart…</div>}
        {err && <div style={{ ...S.overlay, color: PALETTE.red }} data-testid={`${testid}-error`}>{err}</div>}
      </div>

      {tierFull && signal?.risk_summary && (
        <ul style={S.riskList} data-testid={`${testid}-risk-list`}>
          {signal.risk_summary.slice(0, 3).map((r, i) => <li key={i} style={S.riskItem}>{r}</li>)}
        </ul>
      )}
      <footer style={S.foot}>Research only · Not financial advice · HITL required.</footer>
    </section>
  );
}

const S = {
  shell:    { background: "linear-gradient(180deg, rgba(8,32,22,0.55), rgba(4,6,8,0.85))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 18, padding: 16, color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 10, minWidth: 0 },
  head:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: "0.20em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: 18, letterSpacing: "0.04em" },
  titleSub: { color: PALETTE.slate4, fontSize: 12, fontFamily: "'Space Mono', monospace" },
  kpis:     { display: "flex", gap: 6, flexWrap: "wrap" },
  kpiPill:  { fontFamily: "'Space Mono', monospace", fontSize: 11, padding: "3px 9px", borderRadius: 999, border: "1px solid", letterSpacing: "0.06em" },
  overlay:  { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: PALETTE.slate4, fontFamily: "'Space Mono', monospace", fontSize: 12 },
  riskList: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 },
  riskItem: { color: PALETTE.slate3, fontSize: 12, paddingLeft: 12, position: "relative" },
  foot:     { color: PALETTE.slate5, fontFamily: "'Space Mono', monospace", fontSize: 9.5, textAlign: "right" },
};
